import { useEffect, useState } from 'react';
import type { OpenItemsResponse, PaymentAllocationLine, PaymentResponse } from '../../api/types';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getUserById } from '../../api/endpoints/admin';
import { getOpenPaymentItems, getPayment } from '../../api/endpoints/payments';
import { AllocateReceiptCreditDialog } from './AllocateReceiptCreditDialog';
import { canAllocateReceiptCredit } from './receiptCredit';
import { useAllocatePaymentCredit, useRefreshPayments } from './hooks';
import styles from './Payments.module.css';
import { Button } from '../../components/ui/Button';

type CreditEntryProps = {
  paymentId: number;
  onClose: () => void;
  readOnly?: boolean;
  onAllocated?: (payment: PaymentResponse, lines: PaymentAllocationLine[]) => void;
};

export function AllocateReceiptCreditFlow(props: CreditEntryProps) {
  return <ReceiptCreditEntry key={props.paymentId} {...props} />;
}

function ReceiptCreditEntry({
  paymentId,
  onClose,
  onAllocated,
  readOnly = false,
}: CreditEntryProps) {
  const { t } = useTranslation();
  const [snapshot, setSnapshot] = useState<{
    payment: PaymentResponse;
    items: OpenItemsResponse;
    name: string;
  } | null>(null);
  const allocate = useAllocatePaymentCredit();
  const refresh = useRefreshPayments();
  // This entry snapshot stays independent of payment-family invalidation, so a
  // rejected/uncertain submit cannot discard the allocation draft while reconciling.
  const entry = useQuery({
    queryKey: ['receipt-credit-entry', paymentId, readOnly],
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const payment = await getPayment(paymentId);
      if (payment.id !== paymentId) throw new Error('Receipt identity changed.');
      if (!canAllocateReceiptCredit(payment, readOnly)) return { payment, items: null, name: '' };
      const [items, shipper] = await Promise.all([
        getOpenPaymentItems('IN', payment.counterpartyUserId),
        getUserById(payment.counterpartyUserId),
      ]);
      const company = shipper.profile?.companyName;
      const name =
        (typeof company === 'string' && company.trim() ? company : null) ||
        shipper.user.fullName ||
        `Shipper #${payment.counterpartyUserId}`;
      return { payment, items, name };
    },
  });
  // Capture the first fresh entry only. Background reads must never unmount
  // the dialog or replace its draft, pending guard or uncertain-submit fence.
  useEffect(() => {
    if (!snapshot && !entry.isFetching && !entry.isError && entry.data?.items) {
      setSnapshot({ payment: entry.data.payment, items: entry.data.items, name: entry.data.name });
    }
  }, [snapshot, entry.data, entry.isFetching, entry.isError]);
  if (!snapshot)
    return (
      <div className={styles.scrim}>
        <section
          role="dialog"
          aria-modal="true"
          aria-label={t('payments.allocateReceiptCreditFlow.dialogLabel')}
          className={`${styles.dialog} ${styles.compact}`}
        >
          <div className={styles.body}>
            <h2>{t('payments.allocateReceiptCreditFlow.title', { paymentId })}</h2>
            {entry.isError ? (
              <p role="alert">
                {t('payments.allocateReceiptCreditFlow.loadFailed')}{' '}
                <button onClick={() => void entry.refetch()}>{t('payments.allocateReceiptCreditFlow.retry')}</button>
              </p>
            ) : entry.isFetching || !entry.data || entry.data.items ? (
              <p role="status">{t('payments.allocateReceiptCreditFlow.loading')}</p>
            ) : (
              <p role="alert">{t('payments.allocateReceiptCreditFlow.notEligible')}</p>
            )}
            <Button variant="secondary" size="sm" onClick={onClose}>
              {t('common.actions.cancel')}
            </Button>
          </div>
        </section>
      </div>
    );
  return (
    <AllocateReceiptCreditDialog
      readOnly={readOnly}
      payment={snapshot.payment}
      openItems={snapshot.items}
      shipperName={snapshot.name}
      onClose={onClose}
      submitting={allocate.isPending}
      onRefresh={refresh}
      onConfirmed={onAllocated}
      onSubmit={(id, allocations) => allocate.mutateAsync({ paymentId: id, allocations })}
    />
  );
}
